import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { APP_ENV, API_BASE_URL } from "../../config";
import { fetchUserByToken } from "../../api/auth";
import Loader from "../common/Loader";

const COOKIE_MAX_AGE = 216000;
const COOKIE_OPTIONS = {
  "max-age": COOKIE_MAX_AGE,
  path: "/",
  domain: window.location.hostname,
};

const AutoLogin = () => {
  const navigate = useNavigate();

  const getQueryParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      email: params.get("email"),
      token: params.get("token"),
      expiresIn: parseInt(params.get("expiresIn") || "0", 10),
      createdAt: parseInt(params.get("createdAt") || "0", 10),
      id: params.get("id"),
      key: params.get("key"),
      app: params.get("app"),
      lng: params.get("lng") || "fr",
    };
  };

  const setCookie = (name, value, options = {}) => {
    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(
      value
    )};`;
    const opts = { ...COOKIE_OPTIONS, ...options };

    if (opts["max-age"]) cookieString += `max-age=${opts["max-age"]};`;
    if (opts.expires) cookieString += `expires=${opts.expires.toUTCString()};`;
    if (opts.path) cookieString += `path=${opts.path};`;
    if (opts.domain) cookieString += `domain=${opts.domain};`;

    cookieString += "SameSite=None; Secure;";
    document.cookie = cookieString;
  };

  const createAuthCookies = async (userData) => {
    const tokenData = userData.token || {};
    const accessToken = tokenData.access_token;

    const fullUserData = accessToken
      ? userData
      : await fetchUserByToken(tokenData);

    const expiryTime =
      Date.now() + (fullUserData.expiresIn || COOKIE_MAX_AGE) * 1000;

    const authPayload = {
      token: fullUserData.token.access_token,
      id: fullUserData.id,
      email: fullUserData.mainEmail,
      expiresIn: fullUserData.token.expires || COOKIE_MAX_AGE,
      createdAt: fullUserData.createdAt || Date.now(),
      expiry: expiryTime,
      fullName: `${fullUserData.firstName} ${fullUserData.lastName}`,
      scope: fullUserData.token.scope,
    };

    setCookie(`ttp_auth_${APP_ENV}`, JSON.stringify(authPayload));
    setCookie(`ttp_lang_${APP_ENV}`, fullUserData.language || "fr");
    setCookie(
      `ttp_session_${APP_ENV}`,
      JSON.stringify({
        sessionId: fullUserData.token.access_token,
        userId: fullUserData.id,
      })
    );

    setTimeout(() => {
      if (!document.cookie.includes(`ttp_auth_${APP_ENV}=`)) {
        sessionStorage.setItem(
          `ttp_auth_${APP_ENV}_fallback`,
          JSON.stringify(authPayload)
        );
        console.warn("Cookies blocked — fallback to sessionStorage");
      }
    }, 200);
  };

  const fetchUserData = async (token) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/organization/user/getCurrentUser?access_token=${token}`
      );
      const { data } = await res.json();

      if (data) {
        await createAuthCookies(data);

        setTimeout(() => {
          window.location.replace(
            `${window.location.origin}/webview?user=${data.id}&token=${
              data.token.access_token
            }&lng=${data.language || "fr"}`
          );
        }, 500);
      } else {
        throw new Error("Invalid user data");
      }
    } catch (error) {
      console.error("Fetch user error:", error);
      navigate("/login");
    }
  };

  useEffect(() => {
    const params = getQueryParams();

    if (!params.token) {
      navigate("/login");
      return;
    }

    const redirectAfterAuth = () => {
      setTimeout(() => {
        window.location.replace(
          `${window.location.origin}/webview?user=${params.id}&token=${params.token}&lng=${params.lng}`
        );
      }, 500);
    };

    if (!params.email || !params.id) {
      fetchUserData(params.token);
    } else {
      createAuthCookies(params).then(redirectAfterAuth);
    }
  }, []);

  return (
    <div className="AutologinLoader">
      <Loader />
    </div>
  );
};

export default AutoLogin;
